import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useJobs, useAssignJob, type Job } from '../../hooks/useFleet'
import { useUsers, type User } from '../../hooks/useUsers'
import { useDevices, type Device } from '../../hooks/useDevices'
import { useLatestStatusBeacons, type DriverStatusBeacon } from '../../hooks/useWorkforce'
import { SLAClock } from './SLAClock'
import { DriversPanel } from './DriversPanel'
import { JobQueue } from './JobQueue'
import { ExceptionBoard } from './ExceptionBoard'
import { BroadcastModal } from './BroadcastModal'

/// Wave 1 E1.2 — Dispatcher Console.
///
/// Replaces the standalone /jobs view for users with the dispatcher role with
/// a unified workspace: map + job queue + drivers panel. Drag-and-drop
/// assignment, SLA countdowns, and an exception board for delayed/at-risk
/// jobs at the top.
///
/// Layout: top — exception board.
///         center — split (left: job queue, right: map placeholder).
///         bottom — drivers panel with live beacons.
export default function DispatchConsolePage() {
  const { t } = useTranslation()
  const jobsQ = useJobs()
  const usersQ = useUsers()
  const devicesQ = useDevices()
  const beaconsQ = useLatestStatusBeacons()
  const assignJob = useAssignJob()

  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [draggingJobId, setDraggingJobId] = useState<string | null>(null)

  const jobs = jobsQ.data ?? []
  const users = useMemo<User[]>(() => usersQ.data ?? [], [usersQ.data])
  const devices = useMemo<Device[]>(() => devicesQ.data ?? [], [devicesQ.data])
  const beacons: DriverStatusBeacon[] = beaconsQ.data ?? []

  const exceptions = useMemo(() => jobs.filter((j) => isException(j)), [jobs])
  const queue = useMemo(() => jobs.filter((j) => !isCompleted(j)), [jobs])

  const onAssign = async (jobId: string, driverId: string) => {
    const beacon = beacons.find((b) => b.driver_user_id === driverId)
    const deviceId = beacon?.device_id ?? ''
    await assignJob.mutateAsync({ id: jobId, driver_id: driverId, device_id: deviceId })
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 bg-white">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {t('dispatch.title', 'Dispatch')}
          </h1>
          <p className="text-sm text-slate-500">
            {t('dispatch.subtitle', "Assign jobs, track SLAs, and broadcast to your crew.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setBroadcastOpen(true)}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-500 text-white hover:bg-amber-600"
          >
            {t('dispatch.broadcast', 'Broadcast')}
          </button>
        </div>
      </div>

      {/* Exception board */}
      {exceptions.length > 0 && (
        <ExceptionBoard jobs={exceptions} users={users} />
      )}

      {/* Body — left jobs, right map placeholder */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-3 p-3 min-h-0">
        <div className="lg:col-span-1 min-h-0 overflow-hidden flex flex-col">
          <JobQueue
            jobs={queue}
            users={users}
            renderTrailing={(job) => <SLAClock dueAt={job.sla_due_at ?? job.scheduled_at ?? null} />}
            onDragStart={setDraggingJobId}
            onDragEnd={() => setDraggingJobId(null)}
          />
        </div>

        <div className="lg:col-span-2 min-h-0 bg-white rounded-lg border border-slate-200 overflow-hidden relative flex items-center justify-center">
          <div className="text-center text-slate-400">
            <svg
              className="mx-auto w-12 h-12 mb-2"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M9 6.75V15m0-8.25l-6 3v8.25l6-3m0-8.25l6 3M9 15l6 3m0 0l6-3V6.75l-6 3M15 18V9.75" />
            </svg>
            <p className="text-sm">{t('dispatch.mapHint', 'Map preview — clustering ships with Live Map 2.0.')}</p>
          </div>
        </div>
      </div>

      {/* Drivers panel — bottom, scrolls horizontally */}
      <DriversPanel
        users={users}
        devices={devices}
        beacons={beacons}
        draggingJobId={draggingJobId}
        onDropJob={onAssign}
      />

      {broadcastOpen && (
        <BroadcastModal
          drivers={users}
          beacons={beacons}
          onClose={() => setBroadcastOpen(false)}
        />
      )}
    </div>
  )
}

function isException(job: Job): boolean {
  if (job.sla_status === 'at_risk' || job.sla_status === 'breached') return true
  if (!job.sla_due_at) return false
  return new Date(job.sla_due_at).getTime() < Date.now() && !isCompleted(job)
}

function isCompleted(job: Job): boolean {
  return job.status === 'completed' || job.status === 'cancelled'
}
